import { Box, Link, Text } from '@nimbus-ds/components';
import { dateFormat, Format } from 'commons/utils/date';
import {
  useExpirationDate,
  useFrequencyTranslation,
} from 'domains/Billing/Checkout/hooks';
import { useGoToConceptDetails } from 'domains/Billing/Checkout/hooks/useGoToConceptDetails';
import { isPayableCharge } from 'domains/Billing/Checkout/hooks/utils';
import { useFormatCurrency } from 'domains/Billing/hooks';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import RecurrentPaymentDataSkeleton from './Skeleton';
import { PaymentStatusTag, ChargePeriodDetail } from '../../../';
import { RecurrentPaymentChargeDisplayData } from '../../../../hooks';

interface RecurrentPaymentDataProps {
  data: RecurrentPaymentChargeDisplayData;
}

function RecurrentPaymentData({
  data: {
    amountCurrency,
    amountValue,
    description,
    nextExecutionDate,
    status,
    externalReference,
    concept,
    id,
    isProportional,
    fromDate,
    toDate,
    recurringFrequency,
    recurringInterval,
    scheduledDowngradeDate,
  },
}: RecurrentPaymentDataProps) {
  const t = useTranslationBilling(
    'checkout.plansAndSubscriptions.recurrentPaymentsStatus',
  );
  const formatCurrency = useFormatCurrency();
  const { getFrequencyInterval } = useFrequencyTranslation();
  const { expiresIn } = useExpirationDate(concept, externalReference);

  const goToDetails = useGoToConceptDetails(
    'recurrentPaymentStatus',
    concept,
    externalReference,
    id,
  );

  if (!amountValue) return null;

  const periodData = {
    isProportional,
    fromDate,
    toDate,
  };
  const isPaid = status === 'PAID';

  return (
    <Box
      display="flex"
      flexDirection="row"
      paddingX="4"
      paddingY="none"
      justifyContent="space-between"
    >
      <Box display="flex" flexDirection="column" flex="1 1 60%" gap="1-5">
        <Box display="flex" flexDirection="column">
          <Text>{description}</Text>
          {!isPaid && (
            <>
              <ChargePeriodDetail {...periodData} />
              <PaymentStatusTag paymentStatus={status} />
            </>
          )}
          {isPaid &&
            (scheduledDowngradeDate ? (
              <Text fontSize="caption" color="neutral-textLow">
                {t('scheduledDowngrade', {
                  scheduledDowngradeDate: dateFormat(
                    scheduledDowngradeDate,
                    Format.DD_MM_YYYY,
                  ),
                })}
              </Text>
            ) : (
              <Text fontSize="caption" color="neutral-textLow">
                {t('nextExecution', {
                  nextExecutionDate: dateFormat(
                    nextExecutionDate,
                    Format.DD_MM_YYYY,
                    'UTC',
                  ),
                })}
              </Text>
            ))}
        </Box>
        <Link textDecoration="none" appearance="primary" onClick={goToDetails}>
          {t('details')}
        </Link>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        flex="1 1 40%"
        alignItems="flex-end"
        gap="1-5"
      >
        <Box display="inline-flex" flexDirection="row" alignItems="baseline">
          <Text>{formatCurrency(amountValue, amountCurrency)}</Text>
          {!!recurringFrequency && !!recurringInterval && (
            <Text fontSize="caption">
              {`/${getFrequencyInterval({
                recurringFrequency,
                recurringInterval,
              })}`}
            </Text>
          )}
        </Box>
        {!!expiresIn && expiresIn > 0 && status && isPayableCharge(status) && (
          <Text>{t('expiresIn', { expiresIn })}</Text>
        )}
      </Box>
    </Box>
  );
}

RecurrentPaymentData.Skeleton = RecurrentPaymentDataSkeleton;

export default RecurrentPaymentData;
